/**
 * @file SiteHistoryPanel.tsx
 * @description Панель «История публикаций» вида сайта (phase19.16 Р1; SB52,
 * SB53, SB63, SB72, SB87; uiux-design-r1 §6; контракт §6): таблица с
 * 1024px, компактный список ниже; строка открывает панель версии;
 * «Посмотреть» — предпросмотр версии на весь экран, «Вернуть» — окно с
 * предупреждениями, публикует сразу.
 *
 * Самостоятельная панель без маршрута и шапки страницы: открытая версия —
 * снаружи (`openedVersion` / `onOpen`; страница хранит её в адресе).
 * `rowHref` — адрес строки (ссылку на версию можно переслать).
 */

import { Eye, History, Undo2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import type { ListColumn } from "../../ui/responsive-list-types";
import { ResponsiveList } from "../../ui/ResponsiveList";
import { StatusPill } from "../../ui/StatusPill";
import { SitePreviewDialog } from "../preview/SitePreviewDialog";
import { HistoryVersionSheet } from "./HistoryVersionSheet";
import { RollbackDialog } from "./RollbackDialog";
import { useSiteHistory } from "../../../hooks/site-view/useSiteHistory";
import { historyAuthor, historySummary, historyWhen } from "../../../lib/site-view/history";
import type { SiteHistoryItem } from "../../../lib/site-view/types";

const COLUMNS: ListColumn<SiteHistoryItem>[] = [
  { key: "when", header: "Когда", width: "w-[180px]", compact: "title", cell: (r) => historyWhen(r) },
  { key: "who", header: "Кто", width: "w-[200px]", compact: "meta", cell: (r) => historyAuthor(r) },
  { key: "what", header: "Что менялось", compact: "subtitle", cell: (r) => historySummary(r) },
  {
    key: "state",
    header: "",
    width: "w-[120px]",
    compact: "trailing",
    cell: (r) => (r.isCurrent ? <StatusPill tone="success">На сайте</StatusPill> : null),
  },
];

interface Props {
  /** Открытая версия (панель версии); `null` — закрыта. */
  openedVersion: number | null;
  onOpen: (version: number | null) => void;
  /** Адрес строки (ссылку можно переслать); нет — строка открывает панель версии. */
  rowHref: (version: number) => string;
}

export function SiteHistoryPanel({ openedVersion, onOpen, rowHref }: Props) {
  const history = useSiteHistory();
  const [previewing, setPreviewing] = useState<SiteHistoryItem | null>(null);
  const [rollingBack, setRollingBack] = useState<SiteHistoryItem | null>(null);
  const opened = history.items.find((i) => i.version === openedVersion) ?? null;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <ResponsiveList
        aria-label="История публикаций"
        rows={history.items}
        columns={COLUMNS}
        rowKey={(r) => String(r.version)}
        rowHref={(r) => rowHref(r.version)}
        rowLabel={(r) => `Версия от ${historyWhen(r)}`}
        rowActions={(r) => [
          { key: "preview", label: "Посмотреть", icon: Eye, run: () => setPreviewing(r) },
          ...(!r.isCurrent && r.canRollback ? [{ key: "rollback", label: "Вернуть эту версию", icon: Undo2, run: () => setRollingBack(r) }] : []),
        ]}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        empty={{ icon: History, title: "Публикаций ещё не было — сайт в виде по умолчанию." }}
      />
      {history.hasMore ? (
        <Button variant="outline" className="self-center" loading={history.loading} onClick={history.loadMore}>
          Показать ещё
        </Button>
      ) : null}
      <p className="type-caption text-on-surface-variant">Версии храним не меньше {history.retentionMonths} месяцев.</p>
      <HistoryVersionSheet item={opened} onClose={() => onOpen(null)} onPreview={setPreviewing} onRollback={setRollingBack} />
      <RollbackDialog
        item={rollingBack}
        onClose={() => setRollingBack(null)}
        onDone={() => {
          setRollingBack(null);
          onOpen(null);
          history.reload();
        }}
      />
      <SitePreviewDialog
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        title={previewing ? `Версия от ${historyWhen(previewing)}` : "Предпросмотр"}
        source={previewing ? { source: "revision", version: previewing.version } : undefined}
      />
    </div>
  );
}
